import { useCallback, useState } from "react";
import {
  useAccount,
  useChainId,
  useSignTypedData,
  useSwitchNetwork,
} from "wagmi";
import { buildTypedData } from "../helper/x402/typedData";
import { buildX402Header } from "../helper/x402/payload";
import { submitX402Payment } from "../helper/x402/submit";

/**
 * Payment flow state machine for the x402 wallet path.
 *
 * Orchestrates:
 *   1. Network switch (if the connected chain != link.chainId).
 *      Uses wagmi's `useSwitchNetwork` instead of asking the user
 *      to do it manually.
 *   2. Build EIP-712 typed data (ERC-3009 TransferWithAuthorization).
 *   3. Sign via `useSignTypedData` — triggers the wallet's
 *      signature prompt.
 *   4. Base64-encode the x402 envelope into the `X-PAYMENT`
 *      header.
 *   5. POST to the 402 Lambda via `submitX402Payment`.
 *
 * Exposes a `status` that the UI translates into button labels:
 *   idle → switching → signing → submitting → success | error
 *
 * A single `payNow()` call walks the whole flow — no intermediate
 * callbacks / re-renders required on the component side. On error,
 * we stay in 'error' so the page can show a retry button + the
 * mapped message; `reset()` returns to 'idle' for a retry.
 */
export function useX402Pay(link) {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchNetworkAsync } = useSwitchNetwork();
  const { signTypedDataAsync } = useSignTypedData();

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
    setReceipt(null);
  }, []);

  const payNow = useCallback(async () => {
    if (!link || !isConnected || !address) {
      const e = new Error("Connect a wallet to pay.");
      setError(e);
      setStatus("error");
      return;
    }

    try {
      // 1. Chain check + switch. link.chainId is a number from /info.
      // If the wallet is already on the right chain, skip — calling
      // switchNetworkAsync with the current chain still pops a no-op
      // confirm on some wallets.
      const targetChainId = Number(link.chainId);
      if (!Number.isFinite(targetChainId)) {
        throw new Error("Payment link is missing a chain id — contact the creator.");
      }
      if (chainId !== targetChainId) {
        setStatus("switching");
        if (!switchNetworkAsync) {
          throw new Error(
            `Please switch your wallet to ${link.network} (chain ${targetChainId}) and retry.`,
          );
        }
        await switchNetworkAsync(targetChainId);
      }

      // 2 + 3. Build + sign typed data. signTypedDataAsync pops the
      // wallet's signature UI; user rejection throws "User rejected".
      setStatus("signing");
      const typedData = buildTypedData(link, address);
      const signature = await signTypedDataAsync(typedData);

      // 4 + 5. Encode header + POST.
      setStatus("submitting");
      const xPaymentHeader = buildX402Header(
        link,
        typedData.message,
        signature,
      );
      const receiptBody = await submitX402Payment(link, xPaymentHeader);

      setReceipt(receiptBody);
      setStatus("success");
    } catch (err) {
      // Narrow a few common wallet-side errors to friendly strings;
      // fall through to err.message for everything else. The 402
      // Lambda's `submitX402Payment` already carries a user-facing
      // message on its thrown Error.
      let message = err?.message || "Payment failed.";
      if (
        message.toLowerCase().includes("user rejected") ||
        message.toLowerCase().includes("user denied")
      ) {
        message = "Signature request was cancelled.";
      }
      const narrowed = new Error(message);
      narrowed.statusCode = err?.statusCode;
      narrowed.reason = err?.reason;
      setError(narrowed);
      setStatus("error");
    }
  }, [
    link,
    isConnected,
    address,
    chainId,
    switchNetworkAsync,
    signTypedDataAsync,
  ]);

  return { status, error, receipt, payNow, reset };
}
